/**
 * Campaign System - Core Utilities
 *
 * Este arquivo contém toda a lógica de dados do sistema:
 *
 * - leitura (getContadores)
 * - escrita (saveContador, delete, toggle)
 * - cache em memória
 * - fallback para localStorage
 * - cálculo de tempo
 * - temas visuais
 *
 * IMPORTANTE:
 * Este é o único ponto que acessa:
 * - Supabase
 * - localStorage
 *
 * Componentes NÃO devem acessar banco diretamente.
 */

import { buscarRespostaBot } from '../../services/chatbotEngine';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabase';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant' | 'bot';
  timestamp: Date;
  type: 'text' | 'form' | 'success';
}

interface LiveConversationRow {
  id: string;
  visitor_name: string | null;
  visitor_email: string | null;
  visitor_phone: string | null;
  status: string;
  assigned_agent: string | null;
  source: string | null;
  created_at: string;
  updated_at: string;
  whatsapp_fallback_shown?: boolean | null;
}

interface LiveMessageRow {
  id: string;
  conversation_id: string;
  sender_type: 'visitor' | 'bot' | 'agent';
  sender_name: string | null;
  message: string;
  is_read: boolean;
  created_at: string;
}

interface ScheduleData {
  name: string;
  email: string;
  phone: string;
  company: string;
  date: string;
  time: string;
  service: string;
  message: string;
}

interface LeadData {
  name: string;
  email: string;
  phone: string;
  interest: string;
  message: string;
}

interface KnowledgeBase {
  keywords: string[];
  answer: string;
  category: string;
  shouldRequestContact?: boolean;
  shouldShowScheduling?: boolean;
}

interface ChatResponseRow {
  id: string;
  category: string | null;
  keywords: string[];
  response: string;
  enabled: boolean;
  should_request_contact: boolean;
  should_show_scheduling: boolean;
  sort_order: number;
}

interface ChatAssistantRow {
  id: string;
  assistant_name: string | null;
  assistant_status: string | null;
  welcome_message: string | null;
  whatsapp_number: string | null;
  tone: string | null;
  position: string | null;
  response_delay: number | null;
  show_schedule_form: boolean | null;
  show_lead_form: boolean | null;
  is_active: boolean | null;
  responses: Record<string, any> | null;
}

const WHATSAPP_BUTTON_TOKEN = '[WHATSAPP_BUTTON]';
const FALLBACK_TEXT =
  'Todos os atendentes estão em atendimento no momento. Você pode continuar o atendimento pelo WhatsApp.';
const LIVE_CONVERSATION_STORAGE_KEY = 'ibx_live_conversation_id';

// Tempos exclusivos da transferência para atendimento humano (em milissegundos).
// Estes valores não afetam as respostas nem as bolinhas das outras opções do chatbot.
const HUMAN_TRANSFER_TYPING_MS = 1200; // Bolinhas antes da primeira mensagem
const HUMAN_TRANSFER_NEXT_MESSAGE_MS = 1200; // Pausa até a mensagem sobre o tempo de espera
const HUMAN_WAITING_INDICATOR_DELAY_MS = 1000; // Pausa depois da segunda mensagem


const QUICK_RESPONSES = [
  {
    keyword: ['economia', 'economizar', 'reduzir', 'custo', 'valor'],
    response:
      '🎯 **Você pode economizar entre 35-42% nos seus seguros!**\n\nNossa consultoria já identificou milhões em economia para nossos clientes. A análise é **100% gratuita**!\n\n**Principais áreas de economia:**\n• Duplicidades de cobertura\n• Coberturas desnecessárias\n• Valores super dimensionados\n• Falta de competitividade\n\n💡 **Quer descobrir quanto pode economizar?**',
  },
  {
    keyword: ['análise', 'auditoria', 'revisar', 'verificar'],
    response:
      '📊 **Análise Técnica Gratuita dos Seus Seguros**\n\nFazemos uma **auditoria completa** da sua carteira:\n\n✅ **Revisão de coberturas**\n✅ **Identificação de gaps**\n✅ **Análise de preços**\n✅ **Relatório detalhado**\n\n**Resultado:** Economia média de **37%** + proteção otimizada!\n\n🎁 **100% gratuito** - sem compromisso!',
  },
  {
    keyword: ['mal contratado', 'problema', 'sinistro', 'cobertura'],
    response:
      '⚠️ **Seguros Mal Contratados são um Risco Real!**\n\n**Problemas mais comuns:**\n• 🚫 Coberturas insuficientes\n• 💸 Pagamento desnecessário\n• 📋 Cláusulas problemáticas\n• ⏰ Renovação automática prejudicial\n\n**Nossa solução:**\n✅ Análise técnica completa\n✅ Reestruturação inteligente\n✅ Economia garantida\n\n🎯 **Proteja-se agora com análise gratuita!**',
  },
  {
    keyword: ['empresa', 'empresarial', 'negócio', 'cnpj'],
    response:
      '🏢 **Seguros Empresariais Inteligentes**\n\n**Protegemos seu negócio com:**\n• 🔥 Incêndio e riscos diversos\n• 💼 Responsabilidade civil\n• 👥 Vida empresarial\n• 🚗 Frota de veículos\n\n**Diferenciais IBX:**\n✅ Análise técnica especializada\n✅ Economia média de **40%**\n✅ Acompanhamento contínuo\n✅ Suporte em sinistros\n\n💡 **Análise gratuita para sua empresa!**',
  },
  {
    keyword: ['vida', 'previdência', 'aposentadoria'],
    response:
      '👨‍👩‍👧‍👦 **Vida & Previdência Estratégicos**\n\n**Planejamento completo:**\n• 🛡️ Seguro de vida otimizado\n• 💰 Previdência privada\n• 🏥 Planos de saúde\n• 👶 Seguro educacional\n\n**Benefícios IBX:**\n✅ Consultoria personalizada\n✅ Melhores produtos do mercado\n✅ Economia até **35%**\n✅ Acompanhamento vitalício\n\n🎁 **Análise gratuita do seu perfil!**',
  },
  {
    keyword: ['automóvel', 'carro', 'auto', 'veículo'],
    response:
      '🚗 **Seguro Auto Inteligente**\n\n**Cobertura completa com:**\n• 🛡️ Proteção total do veículo\n• 🔧 Assistência 24h premium\n• 💎 Cobertura para terceiros\n• 🏠 Proteção residencial inclusa\n\n**Economia IBX:**\n✅ Até **45%** de desconto\n✅ Coberturas otimizadas\n✅ Zero franquia em serviços\n✅ Atendimento personalizado\n\n🎯 **Cotação gratuita agora!**',
  },
  {
    keyword: ['contrato', 'gestão', 'administrar'],
    response:
      '📋 **Gestão Completa de Contratos**\n\n**Cuidamos de tudo:**\n• 📊 Monitoramento contínuo\n• 📅 Controle de vencimentos\n• 💡 Otimização de coberturas\n• 📈 Relatórios gerenciais\n\n**Resultados comprovados:**\n✅ **37%** economia média\n✅ Zero problemas em renovação\n✅ Proteção sempre atualizada\n✅ Tranquilidade total\n\n🎁 **Análise gratuita da sua carteira!**',
  },
  {
    keyword: ['sinistro', 'recuperação', 'indenização'],
    response:
      '🆘 **Recuperação de Sinistros**\n\n**Maximizamos suas indenizações:**\n• ⚖️ Análise jurídica especializada\n• 📋 Documentação técnica\n• 💪 Negociação com seguradoras\n• 🎯 Recuperação integral\n\n**Nosso histórico:**\n✅ **95%** de sucesso\n✅ Recuperação média **3x maior**\n✅ Processo sem custo inicial\n✅ Pagamento só no sucesso\n\n💡 **Consulta gratuita sobre seu caso!**',
  },
  {
    keyword: ['preço', 'orçamento', 'cotação', 'quanto custa', 'taxa', 'cobrança'],
    response:
      '💰 **Quanto custa contratar a IBX Brasil?**\n\n**Nossa consultoria é 100% GRATUITA!** Não cobramos nenhuma taxa de análise ou honorários.\n\nNossa remuneração vem da comissão das seguradoras — você paga apenas o prêmio do seguro, que trabalhamos para tornar o mais competitivo possível.\n\n**Comparativo de mercado:**\n✅ Análise gratuita (sem custo)\n✅ Economia média de **37%** no prêmio\n✅ Sem taxa de consultoria\n✅ Sem compromisso de contratação\n\n🎯 **Solicite sua cotação gratuita agora!**',
  },
  {
    keyword: ['incêndio', 'incendio', 'patrimônio', 'prédio', 'estabelecimento', 'loja'],
    response:
      '🔥 **Seguro Incêndio Empresarial**\n\nProteja seu patrimônio contra os principais riscos:\n\n**Coberturas disponíveis:**\n• 🔥 Incêndio, explosão e queda de raio\n• 💧 Danos elétricos e alagamento\n• 🌪️ Vendaval e granizo\n• 🚨 Roubo de bens\n• 💼 Lucros cessantes\n• 🏗️ Responsabilidade civil do estabelecimento\n\n**Por que revisar com a IBX?**\n✅ Muitas empresas pagam por coberturas duplicadas\n✅ Valores segurados desatualizados\n✅ Economia média de **40%** após análise\n\n📊 **Análise gratuita da sua apólice atual!**',
  },
  {
    keyword: ['saúde', 'plano de saúde', 'médico', 'hospitalar', 'odontológico', 'dental'],
    response:
      '🏥 **Seguro Saúde e Planos Médicos**\n\nEncontramos o melhor plano para você e sua equipe:\n\n**O que analisamos:**\n• 🏥 Cobertura hospitalar e ambulatorial\n• 🦷 Planos odontológicos\n• 💊 Cobertura de medicamentos\n• 👨‍⚕️ Rede credenciada na sua região\n• 💰 Custo por colaborador\n\n**Nosso diferencial:**\n✅ Comparamos **15+ operadoras**\n✅ Negociação de preços em grupo\n✅ Economia de até **35%**\n✅ Suporte em autorizações e reembolsos\n\n💡 **Análise gratuita para sua empresa!**',
  },
  {
    keyword: ['residencial', 'casa', 'apartamento', 'imóvel', 'residência'],
    response:
      '🏠 **Seguro Residencial Completo**\n\nProteja seu lar com cobertura inteligente:\n\n**Coberturas essenciais:**\n• 🔥 Incêndio e explosão\n• 💧 Danos elétricos e hidráulicos\n• 🚨 Roubo e furto qualificado\n• 🌪️ Vendaval e granizo\n• 🔧 Assistência 24h (encanador, eletricista, chaveiro)\n• 💼 Responsabilidade civil familiar\n\n**Você sabia?**\n✅ Muitos seguros residenciais têm coberturas desnecessárias\n✅ Revisão pode gerar **30-40% de economia**\n✅ Análise gratuita da sua apólice atual\n\n🎯 **Solicite sua análise agora!**',
  },
  {
    keyword: ['responsabilidade civil', 'rc', 'terceiros', 'danos a terceiros', 'processo'],
    response:
      '⚖️ **Seguro de Responsabilidade Civil**\n\nProteja sua empresa de processos e indenizações:\n\n**Modalidades disponíveis:**\n• 🏢 RC do Estabelecimento\n• 👔 RC Profissional (erros e omissões)\n• 🚗 RC Veículos (frota)\n• 🏗️ RC Obras e Construção\n• 💼 D&O (Diretores e Administradores)\n\n**Por que é essencial?**\n✅ Protege contra processos judiciais\n✅ Cobre danos materiais e corporais a terceiros\n✅ Garante a continuidade do negócio\n✅ Exigido em muitos contratos comerciais\n\n📊 **Análise gratuita do seu nível de exposição!**',
  },
  {
    keyword: ['rural', 'agrícola', 'fazenda', 'lavoura', 'pecuária', 'produtor rural'],
    response:
      '🌾 **Seguro Rural e Agrícola**\n\nSoluções completas para o agronegócio:\n\n**Coberturas disponíveis:**\n• 🌱 Seguro agrícola (lavouras)\n• 🐄 Seguro pecuário (rebanho)\n• 🚜 Máquinas e equipamentos agrícolas\n• 🏚️ Benfeitorias e instalações rurais\n• 🌧️ Seguro contra eventos climáticos\n• 💰 Crédito rural e financiamentos\n\n**Diferenciais IBX:**\n✅ Especialistas no setor agro\n✅ Parceria com as principais seguradoras rurais\n✅ Análise técnica da sua propriedade\n✅ Economia média de **35%**\n\n🎯 **Análise gratuita para sua propriedade!**',
  },
  {
    keyword: ['prazo', 'tempo', 'demora', 'quanto tempo', 'rapidez', 'urgente'],
    response:
      '⏱️ **Qual o prazo para análise e contratação?**\n\n**Nosso processo é ágil:**\n\n📋 **Análise inicial:** até **24 horas** após receber suas apólices\n📊 **Relatório completo:** em até **3 dias úteis**\n💼 **Proposta de otimização:** junto com o relatório\n✅ **Contratação:** de **1 a 5 dias úteis** após aprovação\n\n**Para sinistros urgentes:**\n🚨 Atendimento prioritário em até **2 horas**\n📞 WhatsApp direto: **(77) 9 8808-0505**\n\n💡 **Quanto antes você analisar, mais rápido economiza!**',
  },
  {
    keyword: ['documentos', 'documentação', 'apólice', 'o que precisa', 'preciso de'],
    response:
      '📄 **Quais documentos são necessários?**\n\nPara a análise gratuita, basta enviar:\n\n**Para Pessoa Física:**\n• 📋 Apólices atuais (PDF ou foto)\n• 🪪 CPF e RG\n• 📍 Endereço completo\n\n**Para Pessoa Jurídica:**\n• 📋 Apólices atuais\n• 📄 CNPJ e contrato social\n• 🏢 Dados do estabelecimento\n• 💰 Faturamento anual (para RC)\n\n**Não tem as apólices?**\n✅ Podemos solicitar diretamente às seguradoras\n✅ Basta nos autorizar por escrito\n\n🎯 **Envie pelo WhatsApp: (77) 9 8808-0505**',
  },
  {
    keyword: ['confiável', 'confiança', 'idôneo', 'experiência', 'anos de mercado', 'quem são'],
    response:
      '🏆 **Por que confiar na IBX Brasil?**\n\n**Nossa trajetória:**\n✅ Anos de experiência no mercado de seguros\n✅ Mais de **R$ 2,1 milhões** em economia gerada\n✅ **95%** de sucesso em recuperação de sinistros\n✅ Parceria com as **principais seguradoras** do Brasil\n\n**Nossos diferenciais:**\n• 🎯 Consultoria técnica independente\n• 📊 Relatórios detalhados e transparentes\n• 🤝 Acompanhamento contínuo pós-contratação\n• ⚖️ Suporte jurídico em sinistros\n• 💰 Remuneração só via comissão (sem taxa)\n\n💡 **Análise gratuita e sem compromisso!**',
  },
  {
    keyword: ['agendar', 'agendamento', 'consultoria', 'marcar reunião', 'conversar', 'marcar'],
    response:
      '📅 **Agende sua Consultoria Gratuita!**\n\nVamos agendar uma consultoria personalizada para analisar seus seguros e identificar oportunidades de economia.\n\n🎯 **O que faremos:**\n• Análise completa da sua carteira\n• Identificação de oportunidades\n• Proposta de otimização\n• Plano de economia\n\n⏰ **Disponibilidade:**\nSegunda a Sexta: 8h às 17h30\n\n💡 **100% gratuito e sem compromisso!**',
  },
];

const knowledgeBase: KnowledgeBase[] = [
  {
    keywords: ['automóvel', 'carro', 'veículo', 'auto', 'frota'],
    answer:
      'O **Seguro Automóvel** da IBX Brasil oferece cobertura completa para veículos particulares e frotas empresariais. Inclui: proteção contra colisão, roubo e furto, assistência 24h premium, carro reserva, cobertura para terceiros e vidros.\n\nGostaria de receber uma **análise gratuita** do seu seguro atual? Posso conectar você com nossa equipe!',
    category: 'Serviços',
    shouldRequestContact: true,
  },
  {
    keywords: ['vida empresarial', 'vida', 'sócios', 'sucessão'],
    answer:
      'O **Seguro de Vida Empresarial** protege a continuidade do seu negócio em caso de falecimento de sócios ou colaboradores-chave. Oferecemos: cobertura proporcional à participação societária, proteção para key persons e planejamento sucessório.\n\nQuer saber como proteger sua empresa? Deixe seus dados para uma **consultoria gratuita**!',
    category: 'Serviços',
    shouldRequestContact: true,
  },
  {
    keywords: ['gestão', 'contratos', 'organização', 'apólices', 'vencimento'],
    answer:
      'A **Gestão de Contratos** centraliza todas as suas apólices em um único ponto de controle. Oferecemos: organização de vencimentos, renegociação em bloco, análise de coberturas e economia consolidada.\n\nTem várias apólices espalhadas? Vamos **organizar gratuitamente** para você!',
    category: 'Serviços',
    shouldRequestContact: true,
  },
  {
    keywords: ['sinistro', 'recuperação', 'negado', 'indenização', 'recusa'],
    answer:
      'A **Recuperação de Sinistros** atua quando sua seguradora nega ou dificulta o pagamento. Nossa equipe técnica analisa contratos e conduz negociações assertivas. Já recuperamos mais de **R$ 2,1 milhões** para nossos clientes.\n\nTem um sinistro negado? Podemos ajudar! Deixe seus dados para análise **gratuita**.',
    category: 'Serviços',
    shouldRequestContact: true,
  },
  {
    keywords: ['agendar', 'agendamento', 'consultoria', 'marcar reunião', 'conversar', 'marcar'],
    answer:
      '📅 **Agende sua Consultoria Gratuita!**\n\nVamos agendar uma consultoria personalizada para analisar seus seguros e identificar oportunidades de economia.\n\n🎯 **O que faremos:**\n• Análise completa da sua carteira\n• Identificação de oportunidades\n• Proposta de otimização\n• Plano de economia\n\n⏰ **Disponibilidade:**\nSegunda a Sexta: 8h às 17h30\n\n💡 **100% gratuito e sem compromisso!**',
    category: 'Agendamento',
    shouldShowScheduling: true,
  },
  {
    keywords: ['preço', 'custo', 'valor', 'quanto custa', 'taxa', 'cobrança'],
    answer:
      '**Não cobramos taxa de consultoria!** Nossa remuneração vem da comissão das seguradoras, então você paga apenas o prêmio do seguro. Trabalhamos para que esse valor seja o mais competitivo possível.\n\nQuer saber quanto pode economizar? Faça uma **análise gratuita**!',
    category: 'Preços',
    shouldRequestContact: true,
  },
  {
    keywords: ['economia', 'economizar', 'redução', 'desconto'],
    answer:
      'Nossos clientes economizam **em média 35% a 42%** nos prêmios após nossa análise. Já geramos mais de **R$ 2,1 milhões em economia** consolidada!\n\nQuer descobrir sua economia potencial? Deixe seus dados para **análise gratuita**!',
    category: 'Preços',
    shouldRequestContact: true,
  },
  {
    keywords: ['contato', 'falar', 'atendimento', 'equipe'],
    answer:
      'Ótimo! Vou conectar você com nossa equipe para um atendimento personalizado. Nossa análise é **100% gratuita** e você só contrata se achar que vale a pena.',
    category: 'Contato',
    shouldRequestContact: true,
  },
  {
    keywords: ['como funciona', 'processo', 'etapas'],
    answer:
      'Nosso processo é simples:\n\n1️⃣ **Análise gratuita** da sua situação atual\n2️⃣ **Diagnóstico técnico** com identificação de oportunidades\n3️⃣ **Negociação** com seguradoras parceiras\n4️⃣ **Implementação** e acompanhamento\n\nQuer começar? Deixe seus dados!',
    category: 'Processo',
    shouldRequestContact: true,
  },
];


const quickQuestions = [
  'Quero economizar no meu seguro',
  'Preciso de análise gratuita',
  'Quais serviços vocês oferecem?',
  'Como funciona o processo?',
  'Agendar uma consultoria',
  'Falar com atendente',
];

const quickSuggestionCards = [
  { label: 'Economizar no seguro', icon: 'ri-percent-line', message: 'Quero economizar no meu seguro' },
  { label: 'Análise gratuita', icon: 'ri-file-search-line', message: 'Preciso de análise gratuita' },
  { label: 'Nossos serviços', icon: 'ri-briefcase-4-line', message: 'Quais serviços vocês oferecem?' },
  { label: 'Como funciona', icon: 'ri-question-answer-line', message: 'Como funciona o processo?' },
  { label: 'Agendar consultoria', icon: 'ri-calendar-check-line', message: 'Agendar uma consultoria' },
  { label: 'Gestão de riscos', icon: 'ri-shield-check-line', message: 'Assessoria de gestão de riscos empresariais' },
  { label: 'Análise de apólices', icon: 'ri-file-shield-2-line', message: 'Análise técnica de apólices de seguros' },
  { label: 'Seguros empresariais', icon: 'ri-building-2-line', message: 'Contratação de seguros empresariais' },
  { label: 'Falar com atendente', icon: 'ri-customer-service-2-line', message: 'Falar com atendente' },
];

const availableTimes = [
  '08:00',
  '08:30',
  '09:00',
  '09:30',
  '10:00',
  '10:30',
  '11:00',
  '11:30',
  '14:00',
  '14:30',
  '15:00',
  '15:30',
  '16:00',
  '16:30',
  '17:00',
  '17:30',
];

const consultationServices = [
  'Consultoria Técnica Geral',
  'Análise de Apólices Existentes',
  'Seguro Automóvel',
  'Seguro Empresarial',
  'Vida Empresarial',
  'Recuperação de Sinistros',
  'Gestão de Contratos',
];

const getMinDate = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

const getMaxDate = () => {
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 30);
  return maxDate.toISOString().split('T')[0];
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const ChatAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [chatEnabled, setChatEnabled] = useState(true);
  const [chatName, setChatName] = useState('Assistente IBX Brasil');
  const [chatSubtitle, setChatSubtitle] = useState('Assistente Virtual');
  const [welcomeMessage, setWelcomeMessage] = useState(
    'Olá! 👋 Bem-vindo à IBX Brasil. Assessoria e consultoria empresarial. Como posso ajudá-lo hoje?',
  );

  const [isMobile, setIsMobile] = useState(false);

useEffect(() => {
  const checkMobile = () => setIsMobile(window.innerWidth <= 768);
  checkMobile();
  window.addEventListener('resize', checkMobile);
  return () => window.removeEventListener('resize', checkMobile);
}, []);

useEffect(() => {
  if (isOpen) {
    document.body.classList.add('chat-assistant-open');
  } else {
    document.body.classList.remove('chat-assistant-open');
  }

  // 🔥 AQUI É O QUE FALTAVA
  window.dispatchEvent(
    new CustomEvent('ibx-chat-notifications', {
      detail: { disabled: isOpen }
    })
  );

}, [isOpen]);

  const [whatsappNumber, setWhatsappNumber] = useState('5577988080505');
  const [responseDelay, setResponseDelay] = useState(1200);
  
  const [showLeadFormEnabled, setShowLeadFormEnabled] = useState(true);
  const [showScheduleFormEnabled, setShowScheduleFormEnabled] = useState(true);
  const [dbResponses, setDbResponses] = useState<ChatResponseRow[]>([]);

  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isTransferTyping, setIsTransferTyping] = useState(false);
  const [showHumanWaiting, setShowHumanWaiting] = useState(false);
  const humanWaitingTimerRef = useRef<number | null>(null);
  const [leadData, setLeadData] = useState<Partial<LeadData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const visitorTypingTimeoutRef = useRef<any>(null);
  const typingChannelRef = useRef<any>(null);

  /* =========================================================
  CONTROLE DE INATIVIDADE PARA SUGERIR WHATSAPP
  ========================================================= */

  const inactivityTimerRef = useRef<number | null>(null);
  const [inactivityTriggered, setInactivityTriggered] = useState(false);

  // 30 segundos
  const INACTIVITY_TIME = 30000;

  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleData, setScheduleData] = useState<ScheduleData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    date: '',
    time: '',
    service: 'Consultoria Técnica Geral',
    message: '',
  });

  const [liveConversationId, setLiveConversationId] = useState<string | null>(null);
  const [liveConversation, setLiveConversation] = useState<LiveConversationRow | null>(null);
  const [isHumanMode, setIsHumanMode] = useState(false);

  const isConversationClosed = liveConversation?.status === 'closed';

  const playMessageSound = () => {
    try {
      const audio = new Audio('/sounds/message.mp3');
      audio.volume = 0.45;
      audio.play().catch(() => undefined);
    } catch {
      // silencioso
    }
  };

  /* =========================================================
SOM DE ALERTA PARA SUGESTÃO DE WHATSAPP
(INATIVIDADE OU ATENDENTE NÃO RESPONDEU)
========================================================= */

const playWhatsAppAlert = () => {
  try {
    const audio = new Audio('/sounds/whatsapp-alert.mp3');
    audio.volume = 0.6;
    audio.play().catch(() => undefined);
  } catch {
    // silencioso
  }
};

  /* =========================================================
DISPARA SUGESTÃO DE WHATSAPP POR INATIVIDADE
========================================================= */

const triggerInactivityWhatsApp = () => {
  if (inactivityTriggered || isHumanMode) return;

  const message: Message = {
    id: `whatsapp-idle-${Date.now()}`,
    text:
      "Se preferir, podemos continuar o atendimento pelo WhatsApp. Clique no botão abaixo 👇",
    sender: "assistant",
    timestamp: new Date(),
    type: "text",
  };

 const buttonMessage: Message = {
  id: `whatsapp-button-${Date.now()}`,
  text: WHATSAPP_BUTTON_TOKEN,
  sender: "assistant",
  timestamp: new Date(),
  type: "text",
};

setMessages(prev => [...prev, message, buttonMessage]);

playWhatsAppAlert();   // 🔔 TOCA SOM QUANDO O BOTÃO APARECE

setInactivityTriggered(true);
};


/* =========================================================
REINICIA TIMER DE INATIVIDADE
========================================================= */

const resetInactivityTimer = () => {
  if (inactivityTimerRef.current) {
    window.clearTimeout(inactivityTimerRef.current);
  }

  if (!isOpen || isHumanMode) return;

  inactivityTimerRef.current = window.setTimeout(() => {
    triggerInactivityWhatsApp();
  }, INACTIVITY_TIME);
};

  const loadConfig = async () => {
    try {
      const { data, error } = await supabase.from('ibx_chat_assistant').select('*').limit(1).maybeSingle();

      if (error) {
        console.error('Erro ao carregar configuração do chat no Supabase:', error);
        setChatEnabled(true);
        return;
      }

      const config = data as ChatAssistantRow | null;

      if (!config) {
        setChatEnabled(true);
        return;
      }

      setChatEnabled(typeof config.is_active === 'boolean' ? config.is_active : true);

      if (config.assistant_name) setChatName(config.assistant_name);
      if (config.assistant_status) setChatSubtitle(config.assistant_status);
      if (config.welcome_message) setWelcomeMessage(config.welcome_message);
      if (config.whatsapp_number) setWhatsappNumber(config.whatsapp_number);
      if (typeof config.response_delay === 'number') setResponseDelay(config.response_delay);
      if (typeof config.show_lead_form === 'boolean') setShowLeadFormEnabled(config.show_lead_form);
      if (typeof config.show_schedule_form === 'boolean') setShowScheduleFormEnabled(config.show_schedule_form);

      // A posição do chat é fixa à direita, inclusive com configurações antigas no Supabase.

      if (config.responses && typeof config.responses === 'object' && config.responses.welcomeMessage) {
        setWelcomeMessage(config.responses.welcomeMessage);
      }
    } catch (e) {
      console.error('Erro inesperado ao carregar configuração do chat:', e);
      setChatEnabled(true);
    }
  };

  const loadResponses = async () => {
  return;
};

  const loadLiveConversation = async (conversationId: string) => {
    const { data, error } = await supabase
      .from('ibx_chat_conversations')
      .select('*')
      .eq('id', conversationId)
      .maybeSingle();

    if (error) {
      console.error('Erro ao carregar conversa humana:', error);
      return null;
    }

    return (data as LiveConversationRow | null) || null;
  };

 useEffect(() => {
  loadConfig();

  const savedConversationId =
    localStorage.getItem(LIVE_CONVERSATION_STORAGE_KEY);

  if (savedConversationId) {
    setLiveConversationId(savedConversationId);
    setIsHumanMode(true);
  }
}, []);

  useEffect(() => {
    if (!liveConversationId) {
      setLiveConversation(null);
      return;
    }

    const hydrateConversation = async () => {
      const conversation = await loadLiveConversation(liveConversationId);

      if (!conversation) {
        localStorage.removeItem(LIVE_CONVERSATION_STORAGE_KEY);
        setLiveConversationId(null);
        setIsHumanMode(false);
        return;
      }

      setLiveConversation(conversation);
      setIsHumanMode(true);
    };

    hydrateConversation();
  }, [liveConversationId]);

  useEffect(() => {
    setMessages(prev => {
      const hasLiveMessages = prev.some(message => message.id.startsWith('live-'));
      if (prev.length > 0 || isHumanMode || hasLiveMessages) return prev;

      return [
        {
          id: 'welcome-message',
          text: welcomeMessage,
          sender: 'assistant',
          timestamp: new Date(),
          type: 'text',
        },
      ];
    });
  }, [welcomeMessage, isHumanMode]);

  // Ao abrir, mostrar o FINAL das sugestões, inclusive o cartão "Falar com atendente".
  // Rolagem somente no conteúdo do chat, sem deslocar a página principal.
  useEffect(() => {
    if (!isOpen) return;
    const frame = window.requestAnimationFrame(() => {
      const container = messagesContainerRef.current;
      if (container) container.scrollTop = container.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || messages.length <= 1) return;
    const container = messagesContainerRef.current;
    if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
  }, [messages, isTyping, agentTyping, showScheduleForm, isOpen]);

    useEffect(() => {
    if (!liveConversationId) return;

    const loadLiveMessages = async () => {
      const { data, error } = await supabase
        .from('ibx_chat_live_messages')
        .select('*')
        .eq('conversation_id', liveConversationId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Erro ao carregar mensagens do chat ao vivo:', error);
        return;
      }

      const mappedMessages: Message[] = (data || [])
        .filter(
          (msg: LiveMessageRow) => msg.sender_type === 'agent' || msg.sender_type === 'bot',
        )
        .map((msg: LiveMessageRow) => ({
          id: `live-${msg.id}`,
          text: msg.message,
          sender: 'assistant',
          timestamp: new Date(msg.created_at),
          type: 'text',
        }));

      setMessages(prev => {
        const nonLiveMessages = prev.filter(message => !message.id.startsWith('live-'));
        return [...nonLiveMessages, ...mappedMessages];
      });
    };

    loadLiveMessages();

    const messagesChannel = supabase
      .channel(`site-live-chat-${liveConversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'ibx_chat_live_messages',
          filter: `conversation_id=eq.${liveConversationId}`,
        },
        payload => {
          const newMessage = payload.new as LiveMessageRow;

          if (newMessage.sender_type !== 'agent' && newMessage.sender_type !== 'bot') {
            return;
          }

          const mappedMessage: Message = {
            id: `live-${newMessage.id}`,
            text: newMessage.message,
            sender: 'assistant',
            timestamp: new Date(newMessage.created_at),
            type: 'text',
          };

          setMessages(prev => {
            const exists = prev.some(msg => msg.id === mappedMessage.id);
            if (exists) return prev;
            return [...prev, mappedMessage];
          });

          playMessageSound();
          setAgentTyping(false);
        },
      )
      .subscribe();

    const conversationChannel = supabase
      .channel(`site-live-conversation-${liveConversationId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'ibx_chat_conversations',
          filter: `id=eq.${liveConversationId}`,
        },
        payload => {
          const updatedConversation = payload.new as LiveConversationRow;
          setLiveConversation(updatedConversation);
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(messagesChannel);
      supabase.removeChannel(conversationChannel);
    };
  }, [liveConversationId]);

  useEffect(() => {
    if (!liveConversationId) {
      setAgentTyping(false);
      if (typingChannelRef.current) {
        supabase.removeChannel(typingChannelRef.current);
        typingChannelRef.current = null;
      }
      return;
    }

    const typingChannel = supabase.channel(`typing-${liveConversationId}`, {
  config: {
    presence: {
      key: `visitor-${liveConversationId}`,
    },
  },
});

typingChannel
  .on('broadcast', { event: 'admin_typing' }, ({ payload }) => {
    if (payload?.conversationId !== liveConversationId) return;
    setAgentTyping(!!payload?.isTyping);
  })
  .subscribe(async status => {
    if (status === 'SUBSCRIBED') {
      await typingChannel.track({
        userType: 'visitor',
        isTyping: false,
        conversationId: liveConversationId,
        at: new Date().toISOString(),
      });
    }
  });

    typingChannelRef.current = typingChannel;

    return () => {
      supabase.removeChannel(typingChannel);
      typingChannelRef.current = null;
    };
  }, [liveConversationId]);

  const wantsHumanSupport = (text: string) => {
    const normalized = text.toLowerCase().trim();

    const triggers = [
      'falar com atendente',
      'falar com humano',
      'falar com pessoa',
      'quero atendimento humano',
      'quero falar com lidio',
      'quero falar com lídio',
      'falar com consultor',
      'falar com especialista',
      'atendimento humano',
    ];

    return triggers.some(trigger => normalized.includes(trigger));
  };

  const createHumanConversation = async (firstMessage: string) => {
    try {
      const { data: conversation, error: conversationError } = await supabase
        .from('ibx_chat_conversations')
        .insert({
          status: 'open',
          source: 'site_chat',
        })
        .select()
        .single();

      if (conversationError || !conversation) {
        console.error('Erro ao criar conversa humana:', conversationError);
        return null;
      }

      const typedConversation = conversation as LiveConversationRow;

      const { error: messageError } = await supabase.from('ibx_chat_live_messages').insert({
        conversation_id: typedConversation.id,
        sender_type: 'visitor',
        sender_name: 'Visitante',
        message: firstMessage,
        is_read: false,
      });

      if (messageError) {
        console.error('Erro ao salvar primeira mensagem humana:', messageError);
        return null;
      }

      setLiveConversationId(typedConversation.id);
      setLiveConversation(typedConversation);
      setIsHumanMode(true);
      localStorage.setItem(LIVE_CONVERSATION_STORAGE_KEY, typedConversation.id);

      window.setTimeout(async () => {
        try {
          const { data: agentMessages, error: agentError } = await supabase
            .from('ibx_chat_live_messages')
            .select('id')
            .eq('conversation_id', typedConversation.id)
            .eq('sender_type', 'agent')
            .limit(1);

          if (agentError) {
            console.error('Erro ao verificar resposta do atendente:', agentError);
            return;
          }

          const { data: conversationCheck, error: conversationCheckError } = await supabase
            .from('ibx_chat_conversations')
            .select('whatsapp_fallback_shown')
            .eq('id', typedConversation.id)
            .single();

          if (conversationCheckError) {
            console.error('Erro ao verificar fallback da conversa:', conversationCheckError);
            return;
          }

          const hasAgentReply = !!agentMessages && agentMessages.length > 0;
          const fallbackAlreadyShown = conversationCheck?.whatsapp_fallback_shown === true;

          if (!hasAgentReply && !fallbackAlreadyShown) {
            const { error: fallbackMessageError } = await supabase
              .from('ibx_chat_live_messages')
              .insert([
                {
                  conversation_id: typedConversation.id,
                  sender_type: 'bot',
                  sender_name: 'Assistente IBX',
                  message: FALLBACK_TEXT,
                  is_read: false,
                },
                {
                  conversation_id: typedConversation.id,
                  sender_type: 'bot',
                  sender_name: 'Assistente IBX',
                  message: WHATSAPP_BUTTON_TOKEN,
                  is_read: false,
                },
              ]);

            if (fallbackMessageError) {
              console.error('Erro ao salvar fallback do WhatsApp:', fallbackMessageError);
              return;
            }

            const { error: fallbackUpdateError } = await supabase
              .from('ibx_chat_conversations')
              .update({
                whatsapp_fallback_shown: true,
                updated_at: new Date().toISOString(),
              })
              .eq('id', typedConversation.id);

            if (fallbackUpdateError) {
              console.error('Erro ao atualizar status do fallback:', fallbackUpdateError);
            }
          }
        } catch (error) {
          console.error('Erro inesperado no fallback do WhatsApp:', error);
        }
      }, 60000);

      return typedConversation.id;
    } catch (error) {
      console.error('Erro inesperado ao criar atendimento humano:', error);
      return null;
    }
  };

  const findAnswer = (
    question: string,
  ): { answer: string; shouldRequestContact: boolean; shouldShowScheduling: boolean } => {
    const normalizedQuestion = question.toLowerCase().trim();

    for (const item of dbResponses) {
      if (item.keywords?.some(keyword => normalizedQuestion.includes(keyword.toLowerCase()))) {
        return {
          answer: item.response,
          shouldRequestContact: item.should_request_contact,
          shouldShowScheduling: item.should_show_scheduling,
        };
      }
    }

    for (const response of QUICK_RESPONSES) {
      if (response.keyword.some(keyword => normalizedQuestion.includes(keyword))) {
        if (
          response.keyword.some(k =>
            ['agendar', 'agendamento', 'consultoria', 'marcar reunião', 'conversar', 'marcar'].includes(k),
          )
        ) {
          return {
            answer: response.response,
            shouldRequestContact: false,
            shouldShowScheduling: true,
          };
        }

        return {
          answer: response.response,
          shouldRequestContact: true,
          shouldShowScheduling: false,
        };
      }
    }

    for (const item of knowledgeBase) {
      if (item.keywords.some(keyword => normalizedQuestion.includes(keyword))) {
        return {
          answer: item.answer,
          shouldRequestContact: item.shouldRequestContact || false,
          shouldShowScheduling: item.shouldShowScheduling || false,
        };
      }
    }

    return {
      answer:
        'Obrigado pela pergunta! Para te dar a melhor resposta personalizada, que tal falar diretamente com nossa equipe? A consultoria é gratuita e sem compromisso! 😊',
      shouldRequestContact: true,
      shouldShowScheduling: false,
    };
  };

  const saveMessageHistory = async (sender: string, message: string, messageType = 'text') => {
    try {
      await supabase.from('ibx_chat_messages').insert({
        session_id: `session_${window.location.pathname}_${new Date().toISOString().slice(0, 10)}`,
        sender,
        message,
        message_type: messageType,
      });
    } catch (error) {
      console.error('Erro ao salvar histórico do chat:', error);
    }
  };

  const processMessage = async (message: string) => {
    setIsLoading(true);

    const { answer, shouldRequestContact, shouldShowScheduling } = findAnswer(message);
    const assistantMessage: Message = {
      id: `bot-${Date.now()}`,
      text: answer,
      sender: 'bot',
      timestamp: new Date(),
      type: 'text',
    };

    setMessages(prev => [...prev, assistantMessage]);
    setIsTyping(false);
    await saveMessageHistory('bot', answer);

    if (shouldShowScheduling && showScheduleFormEnabled) {
      window.setTimeout(() => {
        setShowScheduleForm(true);
      }, 1000);
    } else if (shouldRequestContact && showLeadFormEnabled) {
      window.setTimeout(() => {
        const formMessage: Message = {
          id: `form-${Date.now()}`,
          text: '',
          sender: 'assistant',
          timestamp: new Date(),
          type: 'form',
        };
        setMessages(prev => [...prev, formMessage]);
      }, 1200);
    }

    setIsLoading(false);
  };

 const handleVisitorTypingRealtime = async (value: string) => {
  if (!typingChannelRef.current || !liveConversationId) return;

  try {
    // 🔥 SEMPRE manda TRUE quando digita
    await typingChannelRef.current.send({
      type: 'broadcast',
      event: 'visitor_typing',
      payload: {
        conversationId: liveConversationId,
        isTyping: value.trim().length > 0,
      },
    });

    // 🔥 limpa timeout antigo
    if (visitorTypingTimeoutRef.current) {
      clearTimeout(visitorTypingTimeoutRef.current);
    }

    // 🔥 garante FALSE depois de parar
    visitorTypingTimeoutRef.current = setTimeout(async () => {
    try {
  await typingChannelRef.current?.send({
    type: 'broadcast',
    event: 'visitor_typing',
    payload: {
      conversationId: liveConversationId,
      isTyping: false,
    },
  });
} catch (error) {
  console.error('Erro ao limpar typing após envio:', error);
}
    }, 1500);

  } catch (error) {
    console.error('Erro typing visitante:', error);
  }
};
  
const handleSendMessage = async (text?: string) => {
  const messageText = (text || inputValue).trim();
  if (!messageText) return;

  // 1) Se já está em conversa humana, envia direto para o atendente
  if (liveConversationId) {
  if (isConversationClosed) return;

  const userMessage: Message = {
    id: `user-${Date.now()}`,
    text: messageText,
    sender: 'user',
    timestamp: new Date(),
    type: 'text',
  };

  setMessages((prev) => [...prev, userMessage]);
  setInputValue('');
  setAgentTyping(false);

  if (visitorTypingTimeoutRef.current) {
    window.clearTimeout(visitorTypingTimeoutRef.current);
  }

  try {
    await typingChannelRef.current?.send({
      type: 'broadcast',
      event: 'visitor_typing',
      payload: {
        conversationId: liveConversationId,
        isTyping: false,
      },
    });
  } catch (error) {
    console.error('Erro ao limpar typing após envio:', error);
  }

  const { error } = await supabase.from('ibx_chat_live_messages').insert({
    conversation_id: liveConversationId,
    sender_type: 'visitor',
    sender_name: 'Visitante',
    message: messageText,
    is_read: false,
  });

  if (error) {
    console.error('Erro ao enviar mensagem para atendimento humano:', error);
  }

  return;
}
  // 2) Se pediu atendimento humano, cria a conversa e para o bot
  if (wantsHumanSupport(messageText) && !liveConversationId) {
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      text: messageText,
      sender: 'user',
      timestamp: new Date(),
      type: 'text',
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');

    // Reinicia somente a sequência visual da transferência humana.
    if (humanWaitingTimerRef.current !== null) clearTimeout(humanWaitingTimerRef.current);
    setShowHumanWaiting(false);
    // O atendimento é solicitado imediatamente; somente a apresentação das mensagens é gradual.
    setIsTransferTyping(true);
    const conversationId = await createHumanConversation(messageText);
    window.setTimeout(() => {
      setIsTransferTyping(false);
      setMessages(prev => [...prev, {
        id: `transfer-${Date.now()}`,
        text: conversationId
          ? '✅ Entendi que você deseja falar com um atendente. Estou transferindo você para nossa equipe. Por favor, aguarde enquanto conectamos você com um especialista. ⏳'
          : 'Não foi possível iniciar o atendimento neste momento. Tente novamente ou entre em contato pelo WhatsApp.',
        sender: 'assistant', timestamp: new Date(), type: 'text',
      }]);
      if (conversationId) {
        // Somente a primeira mensagem usa bolinhas; a segunda aparece após a pausa configurada.
        window.setTimeout(() => {
          setMessages(prev => [...prev, {
            id: `espera-${Date.now()}`,
            text: '⏱️ Tempo estimado de espera: até 5 minutos.\n\nNossos atendentes estão disponíveis de segunda a sexta, das 8h às 18h, e aos sábados, das 8h às 12h.\n\nSe preferir, você também pode falar conosco pelo WhatsApp.',
            sender: 'assistant', timestamp: new Date(), type: 'text',
          }]);
          // O aviso só surge após o intervalo contado da segunda mensagem.
          humanWaitingTimerRef.current = window.setTimeout(() => {
            setShowHumanWaiting(true);
            humanWaitingTimerRef.current = null;
          }, HUMAN_WAITING_INDICATOR_DELAY_MS);
        }, HUMAN_TRANSFER_NEXT_MESSAGE_MS);
      }
    }, HUMAN_TRANSFER_TYPING_MS);
    return;
  }

  // 3) Só aqui entra o chatbot
  const userMessage: Message = {
    id: `user-${Date.now()}`,
    text: messageText,
    sender: 'user',
    timestamp: new Date(),
    type: 'text',
  };

  setMessages((prev) => [...prev, userMessage]);
  setInputValue('');

  setIsTyping(true);

try {
  const resultado = await buscarRespostaBot(messageText);
  const delay = Math.max(700, responseDelay || 1200);

  window.setTimeout(() => {
    if (resultado?.resposta) {
      const botMessage: Message = {
        id: `bot-${Date.now()}`,
        text: resultado.resposta,
        sender: 'assistant',
        timestamp: new Date(),
        type: 'text',
      };

      setMessages((prev) => [...prev, botMessage]);
      setIsTyping(false);
      return;
    }

    const fallbackMessage: Message = {
      id: `bot-${Date.now()}`,
      text: 'Desculpe, não entendi. Por favor, tente novamente ou fale com um atendente.',
      sender: 'assistant',
      timestamp: new Date(),
      type: 'text',
    };

    setMessages((prev) => [...prev, fallbackMessage]);
    setIsTyping(false);
  }, delay);
} catch (error) {
  console.error('Erro ao buscar resposta do bot:', error);

  window.setTimeout(() => {
    const fallbackMessage: Message = {
      id: `bot-${Date.now()}`,
      text: 'Ocorreu um erro ao processar sua mensagem. Tente novamente em instantes.',
      sender: 'assistant',
      timestamp: new Date(),
      type: 'text',
    };

    setMessages((prev) => [...prev, fallbackMessage]);
    setIsTyping(false);
  }, 800);
}
};

  const handleSubmitLead = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!leadData.name || !leadData.email || !leadData.phone) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    setIsSubmitting(true);

    try {
            
      await supabase.from('ibx_chat_leads').insert({
        name: leadData.name,
        email: leadData.email,
        phone: leadData.phone,
        interest: leadData.interest || 'Consultoria Geral',
        message: leadData.message || 'Contato via Chat Assistant',
        source: 'chat_assistant',
      });

      const successMessage: Message = {
        id: `success-${Date.now()}`,
        text:
          '✅ **Perfeito!** Seus dados foram enviados com sucesso.\n\nNossa equipe entrará em contato em até **24 horas** para sua análise gratuita.\n\nEnquanto isso, você pode nos chamar no WhatsApp se tiver urgência!',
        sender: 'assistant',
        timestamp: new Date(),
        type: 'success',
      };

      setMessages(prev => [...prev, successMessage]);
      await saveMessageHistory('assistant', successMessage.text, 'success');

      window.setTimeout(() => {
        const whatsappMessage: Message = {
          id: `success-wa-${Date.now()}`,
          text: WHATSAPP_BUTTON_TOKEN,
          sender: 'assistant',
          timestamp: new Date(),
          type: 'text',
        };
        setMessages(prev => [...prev, whatsappMessage]);
      }, 900);

      setLeadData({});
    } catch (error) {
      console.error('Erro ao enviar lead:', error);
      alert('Ops! Ocorreu um erro ao enviar seus dados. Tente novamente ou entre em contato pelo WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);

      await supabase.from('ibx_chat_agendamentos').insert({
      name: scheduleData.name,
      email: scheduleData.email,
      phone: scheduleData.phone,
      company: scheduleData.company || null,
      service: scheduleData.service,
      date: scheduleData.date,
      time: scheduleData.time,
      message: scheduleData.message || null,
        source: 'chat_assistant',
      });

       const confirmationMessage: Message = {
        id: `schedule-${Date.now()}`,
        text: `✅ **Agendamento Confirmado!**\n\n📅 **${formatDate(scheduleData.date)}** às **${scheduleData.time}**\n\n🎯 **Serviço:** ${scheduleData.service}\n\n**Em breve entraremos em contato para confirmar todos os detalhes!**\n\n📞 Qualquer dúvida: **(77) 9 8808-0505**`,
        sender: 'bot',
        timestamp: new Date(),
        type: 'success',
      };

      setMessages(prev => [...prev, confirmationMessage]);
      await saveMessageHistory('bot', confirmationMessage.text, 'success');

      setShowScheduleForm(false);
      setScheduleData({
        name: '',
        email: '',
        phone: '',
        company: '',
        date: '',
        time: '',
        service: 'Consultoria Técnica Geral',
        message: '',
      });
    } catch (error) {
      console.error('Erro ao agendar:', error);
      const errorMessage: Message = {
        id: `schedule-error-${Date.now()}`,
        text: '❌ **Erro no agendamento.** Tente novamente ou entre em contato pelo WhatsApp: **(77) 9 8808-0505**',
        sender: 'bot',
        timestamp: new Date(),
        type: 'text',
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickQuestion = (question: string) => {
    handleSendMessage(question);
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  const resetHumanConversation = async () => {
    if (humanWaitingTimerRef.current !== null) clearTimeout(humanWaitingTimerRef.current);
    humanWaitingTimerRef.current = null;
    setShowHumanWaiting(false);
    if (visitorTypingTimeoutRef.current) {
      window.clearTimeout(visitorTypingTimeoutRef.current);
    }

    try {
      await typingChannelRef.current?.track({
        userType: 'visitor',
        isTyping: false,
        conversationId: liveConversationId,
        at: new Date().toISOString(),
      });
    } catch {
      // silencioso
    }

    localStorage.removeItem(LIVE_CONVERSATION_STORAGE_KEY);
    setLiveConversationId(null);
    setLiveConversation(null);
    setIsHumanMode(false);
    setIsTransferTyping(false);
    setAgentTyping(false);
    setInputValue('');
    setMessages([
      {
        id: 'welcome-message-reset',
        text: welcomeMessage,
        sender: 'assistant',
        timestamp: new Date(),
        type: 'text',
      },
    ]);
  };

  const widgetPositionClass = 'right-4 md:right-6';
  const buttonPositionClass = 'right-4 md:right-6';

  if (!chatEnabled) return null;

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className={`fixed bottom-24 ${buttonPositionClass} z-40 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 cursor-pointer bg-gradient-to-br from-teal-500 to-teal-600 hover:shadow-teal-500/50 hover:scale-105`}
          aria-label="Abrir chat"
        >
          <div className="absolute inset-0 rounded-full bg-teal-500 animate-ping opacity-25"></div>
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse"></div>
          <i className="ri-message-3-line text-white text-2xl relative z-10"></i>
        </button>
      )}

      {isOpen && createPortal(
        <div
          role="dialog"
          aria-label="Chat da IBX Brasil"
          className={`fixed inset-0 z-[2147483000] flex h-[100dvh] w-full min-w-0 flex-col overflow-hidden overscroll-contain bg-white shadow-2xl md:inset-auto md:bottom-6 ${widgetPositionClass} md:h-[418px] md:w-[326px] md:rounded-2xl`}
        >
          <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white p-4 flex items-center justify-between flex-shrink-0" style={{ paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))' }}>
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <i className={`${isHumanMode ? 'ri-customer-service-2-line' : 'ri-robot-line'} text-white text-lg`}></i>
              </div>
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm truncate">Assistente IBX Brasil</p>
                <div className="flex items-center gap-1.5">
                  {!isConversationClosed && <span className="w-2 h-2 bg-green-300 rounded-full"></span>}
                  <p className="text-teal-100 text-[11px] truncate">
                    {isHumanMode
                      ? isConversationClosed
                        ? 'Atendimento encerrado'
                        : 'Atendimento Humano'
                      : 'Assistente Virtual'}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="w-7 h-7 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors cursor-pointer text-white flex-shrink-0"
              aria-label="Fechar chat"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          <div ref={messagesContainerRef} className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-contain p-3 md:p-4 space-y-4 bg-gray-50">
            {messages.map(message => (
              <div key={message.id}>
                {message.type === 'form' ? (
                  <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                    <div className="text-center mb-4">
                      <div className="w-12 h-12 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-2">
                        <i className="ri-gift-line text-teal-600 text-xl"></i>
                      </div>
                      <h3 className="font-bold text-gray-800 text-sm">Análise Gratuita</h3>
                      <p className="text-xs text-gray-500">Descubra quanto pode economizar</p>
                    </div>

                    <form onSubmit={handleSubmitLead} className="space-y-3" id="chat-leads">
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="Seu nome completo *"
                          value={leadData.name || ''}
                          onChange={e => setLeadData(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          required
                          maxLength={100}
                        />
                      </div>

                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="Seu melhor e-mail *"
                          value={leadData.email || ''}
                          onChange={e => setLeadData(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          required
                          maxLength={100}
                        />
                      </div>

                      <div>
                        <input
                          type="tel"
                          name="phone"
                          placeholder="WhatsApp com DDD *"
                          value={leadData.phone || ''}
                          onChange={e => setLeadData(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                          required
                          maxLength={20}
                        />
                      </div>

                      <div>
                        <select
                          name="interest"
                          value={leadData.interest || ''}
                          onChange={e => setLeadData(prev => ({ ...prev, interest: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        >
                          <option value="">Maior interesse (opcional)</option>
                          <option value="Seguro Automóvel">Seguro Automóvel</option>
                          <option value="Vida Empresarial">Seguro de Vida Empresarial</option>
                          <option value="Seguro Residencial">Seguro Residencial</option>
                          <option value="Seguro Empresarial">Seguro Empresarial</option>
                          <option value="Consultoria em Seguros">Consultoria em Seguros</option>
                          <option value="Assessoria de Gestão de Riscos">Assessoria de Gestão de Riscos</option>
                          <option value="Análise Técnica de Apólices">Análise Técnica de Apólices</option>
                        </select>
                      </div>

                      <div>
                        <textarea
                          name="message"
                          placeholder="Comentário adicional (opcional)"
                          value={leadData.message || ''}
                          onChange={e => setLeadData(prev => ({ ...prev, message: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                          rows={2}
                          maxLength={500}
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-teal-500 hover:bg-teal-600 disabled:bg-gray-400 text-white py-3 rounded-lg font-semibold text-sm transition-colors cursor-pointer disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Enviando...
                          </>
                        ) : (
                          <>
                            <i className="ri-send-plane-fill text-base"></i>
                            Solicitar Análise Gratuita
                          </>
                        )}
                      </button>

                      <p className="text-xs text-gray-500 text-center">* Campos obrigatórios. Resposta em até 24h.</p>
                    </form>
                  </div>
                ) : (
                  <div className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {message.text === WHATSAPP_BUTTON_TOKEN ? (
                      <a
                        href={`https://wa.me/${whatsappNumber}?text=Olá! Estava conversando com o assistente e gostaria de falar com a equipe da IBX Brasil.`}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-4 py-2.5 rounded-lg font-semibold text-sm transition-colors cursor-pointer shadow-sm"
                      >
                        <i className="ri-whatsapp-line text-lg"></i>
                        Falar no WhatsApp
                      </a>
                    ) : (
                      <div
                        className={`max-w-[88%] px-3.5 py-2.5 rounded-2xl shadow-sm ${
                          message.sender === 'user'
                            ? 'bg-teal-500 text-white rounded-br-none'
                            : message.type === 'success'
                              ? 'bg-green-50 text-green-800 border border-green-200 rounded-bl-none'
                              : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                        }`}
                      >
                        {message.sender !== 'user' && (
                          <p className={`text-[11px] font-medium mb-1 ${message.type === 'success' ? 'text-green-700' : 'text-teal-600'}`}>
                            {isHumanMode ? 'Atendente IBX Brasil' : 'Assistente IBX Brasil'}
                          </p>
                        )}
                        <p className="text-sm leading-relaxed whitespace-pre-line">
                          {message.text
                            .replace(WHATSAPP_BUTTON_TOKEN, '')
                            .split('**')
                            .map((part, i) => (i % 2 === 1 ? <strong key={i}>{part}</strong> : part))}
                        </p>
                        <p
                          className={`text-[11px] mt-1 ${
                            message.sender === 'user'
                              ? 'text-teal-100'
                              : message.type === 'success'
                                ? 'text-green-600'
                                : 'text-gray-400'
                          }`}
                        >
                          {formatTime(message.timestamp)}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {agentTyping && isHumanMode && !isConversationClosed && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-none shadow-sm">
                  <p className="text-xs text-teal-600 font-medium mb-1">Atendente está digitando...</p>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            {((isTyping && !isHumanMode) || isTransferTyping) && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-none shadow-sm">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            {isConversationClosed && isHumanMode && (
              <div className="flex justify-start">
                <div className="max-w-[88%] px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  Este atendimento foi encerrado. Você pode iniciar uma nova conversa ou continuar pelo WhatsApp.
                </div>
              </div>
            )}

            {messages.length === 1 && !isHumanMode && (
              <div className="space-y-2">
                <p className="text-xs text-gray-500 text-center">Sugestões rápidas:</p>
                <a
                  href={`https://wa.me/${whatsappNumber}?text=${encodeURIComponent('Olá! Gostaria de saber mais sobre a assessoria e consultoria de seguros da IBX Brasil.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Conhecer nossa assessoria pelo WhatsApp"
                  className="group flex w-full items-center gap-3 rounded-xl bg-gradient-to-r from-teal-500 to-teal-600 px-3 py-3 text-white shadow-md transition-all hover:from-teal-600 hover:to-teal-700 hover:shadow-lg"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white/20">
                    <i className="ri-shield-check-line text-xl" aria-hidden="true"></i>
                  </span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="block text-[13px] font-semibold leading-snug">Conhecer nossa assessoria</span>
                    <span className="mt-1 block text-[11px] leading-snug text-teal-50">Conheça nossos serviços e soluções</span>
                  </span>
                  <i className="ri-external-link-line shrink-0 text-base text-white/90" aria-hidden="true"></i>
                </a>

                <div className="grid grid-cols-2 gap-2">
                  {quickSuggestionCards.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => handleQuickQuestion(item.message)}
                      className="bg-white hover:bg-teal-50 border border-gray-200 hover:border-teal-300 rounded-xl p-3 text-left transition-all group cursor-pointer"
                    >
                      <i className={`${item.icon} text-teal-500 text-lg mb-1.5 block`}></i>
                      <span className="text-xs text-gray-700 group-hover:text-teal-700 font-medium leading-snug block">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {showHumanWaiting && isHumanMode && !isConversationClosed && liveConversation?.status === 'open' && messages.some(message => message.id.startsWith('espera-')) && (
              <div className="flex justify-center">
                <span className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-[11px] font-medium text-amber-700">
                  <i className="ri-time-line mr-1" aria-hidden="true"></i>Aguardando atendente...
                </span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {showScheduleForm && !isHumanMode && (
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-t border-gray-200">
              <div className="bg-white rounded-xl p-4 border border-blue-200 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-calendar-line text-blue-600"></i>
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm">Agendar Consultoria Gratuita</h3>
                </div>

                <form onSubmit={handleScheduleSubmit} className="space-y-3" id="chat-agendamento">
                  <div className="grid grid-cols-2 gap-3">
                    <input type="text" name="name" placeholder="Nome completo" value={scheduleData.name} onChange={e => setScheduleData(prev => ({ ...prev, name: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" required maxLength={100} />
                    <input type="email" name="email" placeholder="E-mail" value={scheduleData.email} onChange={e => setScheduleData(prev => ({ ...prev, email: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" required maxLength={100} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="tel" name="phone" placeholder="WhatsApp" value={scheduleData.phone} onChange={e => setScheduleData(prev => ({ ...prev, phone: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" required maxLength={20} />
                    <input type="text" name="company" placeholder="Empresa (opcional)" value={scheduleData.company} onChange={e => setScheduleData(prev => ({ ...prev, company: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" maxLength={100} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <input type="date" name="date" value={scheduleData.date} onChange={e => setScheduleData(prev => ({ ...prev, date: e.target.value }))} min={getMinDate()} max={getMaxDate()} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
                    <select name="time" value={scheduleData.time} onChange={e => setScheduleData(prev => ({ ...prev, time: e.target.value }))} className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" required>
                      <option value="">Selecione horário</option>
                      {availableTimes.map(time => (<option key={time} value={time}>{time}</option>))}
                    </select>
                  </div>
                  <select name="service" value={scheduleData.service} onChange={e => setScheduleData(prev => ({ ...prev, service: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                    {consultationServices.map(service => (<option key={service} value={service}>{service}</option>))}
                  </select>
                  <textarea name="message" placeholder="Conte-nos um pouco sobre sua necessidade... (opcional)" value={scheduleData.message} onChange={e => setScheduleData(prev => ({ ...prev, message: e.target.value.slice(0, 500) }))} rows={3} maxLength={500} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none" />
                  <div className="flex gap-2">
                    <button type="button" onClick={() => setShowScheduleForm(false)} className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm whitespace-nowrap cursor-pointer">Cancelar</button>
                    <button type="submit" disabled={isLoading} className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-colors text-sm font-medium whitespace-nowrap disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed">{isLoading ? 'Agendando...' : 'Confirmar'}</button>
                  </div>
                </form>
                <div className="mt-3 text-xs text-gray-500 text-center">📞 Dúvidas? WhatsApp: (77) 9 8808-0505</div>
              </div>
            </div>
          )}

          <div className="p-3 md:p-4 bg-white border-t border-gray-200 flex-shrink-0" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))' }}>
            {isHumanMode && isConversationClosed ? (
              <div className="space-y-2">
                <button type="button" onClick={resetHumanConversation} className="w-full px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors text-sm font-medium cursor-pointer">Iniciar nova conversa</button>
                <a href={`https://wa.me/${whatsappNumber}?text=Olá! Gostaria de continuar meu atendimento com a equipe da IBX Brasil.`} target="_blank" rel="nofollow noreferrer" className="block w-full text-center px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm font-medium cursor-pointer">Falar no WhatsApp</a>
              </div>
            ) : (
              <form
                onSubmit={e => {
                  e.preventDefault();
                  handleSendMessage();
                }}
                className="flex gap-2"
              >
                <input
                  ref={inputRef}
                  type="text"
                  value={inputValue}
                  onChange={(e) => {
                    const value = e.target.value;
                    setInputValue(value);
                    handleVisitorTypingRealtime(value);
                    resetInactivityTimer();
                  }}
                  placeholder={isConversationClosed ? 'Atendimento encerrado' : isHumanMode ? 'Digite sua mensagem para o atendente...' : 'Digite sua mensagem...'}
                  className="flex-1 min-w-0 px-3 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                  maxLength={200}
                  disabled={isConversationClosed}
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isConversationClosed}
                  className="w-10 h-10 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-300 text-white rounded-lg flex items-center justify-center transition-colors cursor-pointer disabled:cursor-not-allowed flex-shrink-0"
                >
                  <i className="ri-send-plane-fill text-lg"></i>
                </button>
              </form>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default ChatAssistant;